import React from 'react';
import classNames from 'classnames';
import moment from 'moment';
import { formatNumber } from 'humanize-plus';
import { titleize } from 'inflection';
import { useApolloClient } from '@apollo/client';

import ContestBadge from '@buyside/components/presentation/ContestBadge/contestBadge';
import ErrorBoundary from '@buyside/components/presentation/ErrorBoundary/errorBoundary';
import FlagItemModalContainer from '@buyside/components/container/FlagItemModalContainer';
import FollowButton, {
  FollowableType
} from '@buyside/components/presentation/FollowButton/followButton';
import IdeaOverviewLoading from '@buyside/components/presentation/IdeaShowPage/IdeaOverview/ideaOverviewLoading';
import IdeaReadingListButton from '@buyside/components/presentation/IdeaReadingListButton/ideaReadingListButton';
import Link from '@buyside/components/presentation/Link/link';
import RatingArea from '@buyside/components/presentation/IdeaShowPage/Rating/ratingArea';
import { BUTTON_SIZES, BUTTON_TYPES } from '@buyside/components/presentation/Button/button';

import FlagIcon from '@buyside/svgs/flag.svg';
import { isEmpty } from '@buyside/util/lodashAlt';
import separateWithDots from '@buyside/util/separateWithDots';
import { entityUrl, ideaSearchUrl, openCompanyPageUrl } from '@buyside/util/urlHelper';

import { IdeaCoreQuery, IdeaPositionsQuery } from '@interfaces/graphql-typings';

const IdeaCore = require('buyside/graphql/queries/ideaShowPage/IdeaCore');
const IdeaPositions = require('buyside/graphql/queries/ideaShowPage/IdeaPositions');

import styles from './ideaOverviewStyles.scss';

// There will be a separate PR for the section above elevator pitch.
// Please skip review of that section. - TMA (21 Jan 2020)
const IdeaOverview: React.FC<{
  canRateIdea: boolean;
  ideaId: string;
  isAuthor: boolean;
  isPublic: boolean;
  loading: boolean;
}> = ({ canRateIdea, ideaId, isAuthor, isPublic, loading }) => {
  const client = useApolloClient();

  const cachedIdea = client.readQuery<IdeaCoreQuery>({
    query: IdeaCore,
    variables: { ideaId }
  })?.idea;

  const cachedPosIdea = client.readQuery<IdeaPositionsQuery>({
    query: IdeaPositions,
    variables: { ideaId }
  })?.idea;

  if (loading) return <IdeaOverviewLoading />;

  const {
    elevatorPitch,
    awardWinningContestIdeas,
    openedAt,
    lastUpdatedAt,
    tagList,
    weeklyTopIdea
  } = cachedIdea || {};
  const { positions } = cachedPosIdea || {};

  const isDebtOrPreferred = positions?.every((position): boolean =>
    Boolean(position?.asset?.isCredit)
  );

  return (
    <div className={styles.ideaShowCard} data-heap-segment="idea-show-card">
      <div>
        {positions?.map(
          ({ asset, userCreditData }, idx): React.ReactElement => {
            const assetName = asset?.name;
            const entityId = asset?.entity?.id || '';

            return (
              <div key={idx} className={styles.showCardHeader}>
                <a href={isPublic ? openCompanyPageUrl(entityId) : entityUrl(entityId)}>
                  {assetName && titleize(assetName)}
                  {userCreditData && (
                    <span>
                      {userCreditData.couponRate && (
                        <span className={styles.creditInfo}>
                          &bull; {formatNumber(userCreditData.couponRate, 2)}%
                        </span>
                      )}
                      {userCreditData.maturityDate && (
                        <span className={styles.creditInfo}>
                          &bull; {moment(userCreditData.maturityDate).format('YYYY')}
                        </span>
                      )}
                    </span>
                  )}
                </a>
                {!isPublic && (
                  <div className={styles.followButton}>
                    <FollowButton
                      type={FollowableType.ENTITY}
                      id={entityId}
                      size={BUTTON_SIZES.TINIEST}
                      analyticsTag="follow-company"
                    />
                  </div>
                )}
              </div>
            );
          }
        )}
      </div>
      <div className={styles.ideaBasicInfo}>
        <div className={styles.ideaBasicInfoItem}>
          Asset Class:{' '}
          <strong>
            {separateWithDots(positions?.map(position => position.asset?.assetClassName))}
          </strong>
        </div>
        <div className={styles.ideaBasicInfoItem}>
          {isDebtOrPreferred ? 'CUSIP: ' : 'Symbol: '}
          <strong>{separateWithDots(positions?.map(position => position.asset?.symbol))}</strong>
        </div>
        <div className={styles.ideaBasicInfoItem}>
          Updated: <strong>{moment(lastUpdatedAt).format('M/D/YYYY')}</strong>
        </div>
        <div className={styles.ideaBasicInfoItem}>
          Submitted: <strong>{moment(openedAt).format('M/D/YYYY')}</strong>
        </div>
      </div>
      <div>
        <span>
          {weeklyTopIdea && <img src="/images/pro/wti_badge.png" className={styles.contestImage} />}
          {awardWinningContestIdeas?.map(contestIdea => (
            <ContestBadge contestIdea={contestIdea} />
          ))}
        </span>
      </div>
      {elevatorPitch && (
        <div
          className={classNames(
            { [styles.elevatorPitch]: elevatorPitch.length >= 60 },
            { [styles.largeElevatorPitch]: elevatorPitch.length < 60 }
          )}
        >
          <span data-test="elevator-pitch">{elevatorPitch}</span>
        </div>
      )}
      {!isEmpty(tagList) && (
        <div className={styles.tagSection}>
          {tagList?.map(
            (tag): React.ReactElement => (
              <Link
                key={tag}
                className={styles.tag}
                to={ideaSearchUrl(`tag=${tag}`)}
              >{`#${tag}`}</Link>
            )
          )}
        </div>
      )}
      <div className={styles.actionsContainer}>
        <ErrorBoundary pageName="idea rating area">
          <RatingArea
            canRateIdea={canRateIdea}
            ideaId={ideaId}
            isAuthor={isAuthor}
            isPublic={isPublic}
          />
        </ErrorBoundary>
        {!isAuthor && !isPublic && (
          <div className={styles.bookMarkFlag}>
            <IdeaReadingListButton ideaId={ideaId} />
            <div className={styles.flagIdea}>
              <FlagItemModalContainer
                buttonOptions={{
                  text: 'Flag Idea',
                  type: BUTTON_TYPES.LINK,
                  icon: FlagIcon
                }}
                itemId={ideaId}
                type="Idea"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default IdeaOverview;
